import {LeafletVectorSource} from '../classes/leaflet-vector-source';
import {LeafletMapLayer} from '../classes/leaflet-map-layer';
import {LeafletFeature} from '../classes/leaflet-feature';
import L, {LatLng, Layer} from 'leaflet';
import {LayerStyleService} from '../services/layer-style.service';
import {RefineryStatus} from '../../report/classes/refinery-status';
import {PopoverElement} from '../../../classes/popover-element';
import {DecimalPipe} from '@angular/common';

export class OngRefinerySource extends LeafletVectorSource<RefineryStatus> {
    private numberPipe = new DecimalPipe('en-us');

    constructor(layerInfo: LeafletMapLayer) {
        super(layerInfo);
    }

    processFeatures(features: any[] | any | undefined): void {
        this.leafletFeatures = features.map((f) => {
            const feature = new LeafletFeature<RefineryStatus>(f).buildPopover(this.layerInfo);

            feature.properties = new RefineryStatus(feature.properties);

            const data = [
                new PopoverElement().setTitle().setValue(feature.properties.name),
                new PopoverElement()
                    .setLabel('Total Capacity')
                    .setValue(`${this.numberPipe.transform(feature.properties.total_capacity, '1.0-0')}  bpd`),
                new PopoverElement()
                    .setLabel('Impacted Capacity')
                    .setValue(`${this.numberPipe.transform(feature.properties.offline_capacity, '1.0-0')}  bpd`),
                new PopoverElement()
                    .setLabel('Impacted Percent')
                    .setValue(`${((feature.properties.offline_capacity / feature.properties.total_capacity) * 100).toFixed(2)} %`),
            ];

            feature.subFeatures = [
                {
                    id: feature.properties.facility_id,
                    popoverData: data,
                },
            ];

            return feature;
        });

        const style = (feature: any, latlng: LatLng): Layer => {
            return L.marker(latlng, {
                icon: getStyle(feature),
            });
        };

        const config = {
            pointToLayer: style,
            onEachFeature: this.initializePopoverInteractions.bind(this),
        };

        this.source = L.geoJSON(this.leafletFeatures as any, config as any);
    }

    public changeOpacity(opacity: number): void {
        if (!this.source) {
            return;
        }

        this.layerInfo.opacity = opacity;
        this.source.getLayers().forEach((l) => {
            (l as any).setOpacity(opacity);
        });
    }
}

function getStyle(feature: LeafletFeature): any {
    const ref = feature.properties;
    
    let status = 'impacted';
    if (ref.total_units === ref.online_units) status = 'online';
    else if (ref.total_units === ref.offline_units) status = 'offline';

    const alertCount = ref.refinery_alert_count;
    const plannedCount = ref.planned_outage_count;

    let outlineStyle = '';
    if (alertCount > 0 && plannedCount <= 0) {
        outlineStyle = 'outline-alert';
    } else if (alertCount <= 0 && plannedCount > 0) {
        outlineStyle = 'outline-planned';
    } else if (alertCount > 0 && plannedCount > 0) {
        outlineStyle = 'outline-alert';
    }

    const radius = getRadius(feature.properties.total_capacity);
    const key = `ogcRefinery-25-${status}-${outlineStyle}`;
    let style = LayerStyleService.layerStyles.get(key);

    if (!style) {
        let iconName = 'warning';
        if (status === 'online') iconName = 'check-fat';
        else if (status === 'offline') iconName = 'prohibit';

        style = L.divIcon({
            iconSize: [20, 20],
            className: `ph-fill ph-${iconName} refinery-status-icon ${status} ${outlineStyle}`,
        });

        LayerStyleService.layerStyles.set(key, style);
    }
    return style;
}

function getRadius(capacity: number): any {
    if (capacity > 250_000) {
        return {
            size: 40,
            fontSize: 20,
        };
    } else if (capacity > 110_000) {
        return {
            size: 35,
            fontSize: 18,
        };
    } else if (capacity > 50_000) {
        return {
            size: 30,
            fontSize: 15,
        };
    } else {
        return {
            size: 25,
            fontSize: 12,
        };
    }
}
